import { useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useAtomValue } from 'jotai';

import { DirectMessageService } from '@/components/pages/conversations/SocialInbox/services/DirectMessageService';

import { useWindowFocus } from '@/components/pages/conversations/SocialInbox/hooks/useWindowFocus';
import { useDMConversationMessages } from '@/utils/hooks/swr/conversations/useDMConversationMessages';
import { useSWRSocket, WEBSOCKET_EVENTS } from '@/utils/hooks/swr/useSWRSocket';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import {
    DMConversation,
    DMConversationRefreshEvent,
    DMInboundMessageEvent
} from '@/components/pages/conversations/SocialInbox/types';

import { openedMessageAtom } from '@/components/pages/conversations/SocialInbox/atoms';

import {
    InfiniteScroll,
    InfiniteScrollRef
} from '@/components/composite/InfiniteScroll/InfiniteScroll';
import { DirectMessageBubble } from '@/components/pages/conversations/SocialInbox/components/SocialInboxChatView/SocialInboxMessages/SocialInboxDirectMessages/DirectMessageBubble/DirectMessageBubble';
import { DirectMessageInput } from '@/components/pages/conversations/SocialInbox/components/SocialInboxChatView/SocialInboxMessages/SocialInboxDirectMessages/DirectMessageInput/DirectMessageInput';
import { SocialInboxDirectMessagesSkeleton } from '@/components/pages/conversations/SocialInbox/components/SocialInboxChatView/SocialInboxMessages/SocialInboxDirectMessages/SocialInboxDirectMessagesSkeleton';

export const SocialInboxDirectMessages = () => {
    const openedConversation = useAtomValue(openedMessageAtom) as DMConversation;
    const { activeSocialProfile } = useActiveSocialProfile();
    const { data, isLoading, isLoadingMore, isLoadMore, loadMore } = useDMConversationMessages({
        activeSPID: activeSocialProfile?.id,
        conversationId: openedConversation.meta_conversation_id
    });
    const { data: event } = useSWRSocket<DMInboundMessageEvent | DMConversationRefreshEvent>();

    useWindowFocus({
        onFocus: () =>
            DirectMessageService.initiateConversationMessageFetchJob(
                activeSocialProfile.id,
                openedConversation.meta_conversation_id
            )
    });

    const formMethods = useForm({ mode: 'all', defaultValues: { message: '' } });
    const infiniteScroll = useRef<InfiniteScrollRef>(null);
    const previousConversationId = useRef<string | null>(null);

    const handleScrollToBottom = () => {
        if (infiniteScroll.current) {
            setTimeout(() => {
                infiniteScroll.current?.scrollToBottom();
            }, 100);
        }
    };

    // Scroll to bottom on conversation switch
    useEffect(() => {
        if (openedConversation.meta_conversation_id !== previousConversationId.current) {
            handleScrollToBottom();

            previousConversationId.current = openedConversation.meta_conversation_id;
        }
    }, [openedConversation.meta_conversation_id, data.length]);

    useEffect(() => {
        if (
            event?.event === WEBSOCKET_EVENTS.INSTAGRAM_INBOUND_MESSAGE &&
            event?.data?.message_data?.meta_conversation_id ===
                openedConversation.meta_conversation_id
        ) {
            handleScrollToBottom();
        }
    }, [event, openedConversation.meta_conversation_id]);

    return (
        <FormProvider {...formMethods}>
            {isLoading ? (
                <SocialInboxDirectMessagesSkeleton />
            ) : (
                <InfiniteScroll
                    ref={infiniteScroll}
                    data={data}
                    loadMore={loadMore}
                    hasMore={isLoadMore}
                    isLoadingMore={isLoadingMore}
                    className="px-4 sm:px-10 [&>div>div:last-child]:pb-4 sm:[&>div>div:last-child]:pb-10"
                    renderItem={(message) => (
                        <DirectMessageBubble key={message.id} message={message} className="mt-10" />
                    )}
                    inverse
                />
            )}

            <DirectMessageInput onSend={handleScrollToBottom} disabled={isLoading} />
        </FormProvider>
    );
};
